import { flexRender, Table } from '@tanstack/react-table';

import { FileDeletionType, UploadProcessingStatus } from '../../../../../../../../../backend/src/types/types';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { TablePagination } from '@/new/components/table/table-pagination';

export type LogEventType = 'upload' | 'deletion';

export interface BaseLogEvent {
  id: string;
  type: LogEventType;
  date: string;
  userEmail: string;
}

export interface UploadLogEvent extends BaseLogEvent {
  type: 'upload';
  status: UploadProcessingStatus;
  attempted: number;
  successful: number;
  skipped: number;
}

export interface DeletionLogEvent extends BaseLogEvent {
  type: 'deletion';
  count: number;
  deletionType: FileDeletionType;
}

export type LogEvent = UploadLogEvent | DeletionLogEvent;

interface FileHistoryLogProps {
  isLogLoading: boolean;
  logEvents: LogEvent[];
  logTable: Table<LogEvent>;
  onEventClick: (event: LogEvent) => void;
}

export function FileHistoryLog({ isLogLoading, logEvents, logTable, onEventClick }: FileHistoryLogProps) {
  if (isLogLoading) {
    return (
      <TableComponent>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Summary</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-28" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-36" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-6 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-6 w-64" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>
    );
  }

  if (logEvents.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-500">No events to display</div>;
  }

  return (
    <>
      <TableComponent>
        <TableHeader>
          {logTable.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {logTable.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className="hover:bg-muted cursor-pointer" onClick={() => onEventClick(row.original)}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>
      {logEvents.length > 0 && <TablePagination table={logTable} plural="events" />}
    </>
  );
}
